import { skipToken } from '@apollo/client/react'
import { uniqBy } from 'lodash-es'
import { useCallback, useEffect, useMemo } from 'react'
import { useMyInfo } from 'src/services/apollo/auth'
import {
  type DateRange,
  type GenerationModel,
  GenerationModelType,
  type ListGenerationModelsQueryVariables,
} from 'src/services/apollo/generated/graphql'
import { useInfiniteListGenerationModels } from 'src/services/apollo/generation-model'
import {
  getPosSeqAndFeedForModelListQuery,
  translateOrderOptionToQueryOrderBy,
} from 'src/services/helpers/orderOptionParams'
import { $globalSearching } from 'src/stores/signal'
import { getNodesFromConnection } from 'src/utils/graphql'
import { normalizeSearch } from 'src/utils/string'

export interface GenerationModelFeedResult {
  models: GenerationModel[]
  loading: boolean
  loadingMore: boolean
  hasNext: boolean
  loadMore: () => void
  mutateModels: () => void
}

export interface UseGenerationModelFeedArgs {
  tag?: string
  tackId?: string
  keyword?: string
  order?: string
  type?: string
  challenge?: number
  authorId?: string
  pageSize?: number
  extraArgs?: ListGenerationModelsQueryVariables
  timeRange?: DateRange
}

export function useGenerationModelFeed(
  args: UseGenerationModelFeedArgs | null,
): GenerationModelFeedResult {
  const {
    tag,
    tackId,
    keyword,
    order,
    type,
    authorId,
    timeRange,
    pageSize = 24,
    extraArgs,
  } = args ?? {}

  const { loading: userLoading } = useMyInfo()

  const types = useMemo(
    () =>
      type
        ? type
            .split(',')
            .filter(v =>
              Object.values(GenerationModelType).includes(
                v as GenerationModelType,
              ),
            )
        : undefined,
    [type],
  )

  const { posSeq: isPosSeq, feed } = getPosSeqAndFeedForModelListQuery({
    order,
    keyword,
  })

  // stabilize timeRange reference
  const timeRangeHash = useMemo(() => JSON.stringify(timeRange), [timeRange])

  const queryArgs: Parameters<typeof useInfiniteListGenerationModels>[0] =
    useMemo(() => {
      if (args === null || userLoading) return skipToken

      const vars: ListGenerationModelsQueryVariables = {
        ...extraArgs,
        tag,
        tackId,
        keyword:
          typeof keyword === 'string' ? normalizeSearch(keyword) : undefined,
        authorId,
        types: types as GenerationModelType[],
        feed,
        orderBy: translateOrderOptionToQueryOrderBy(order),
        timeRange,
      }

      if (isPosSeq) {
        vars.first = pageSize
      } else {
        vars.last = pageSize
      }

      if (tag === 'challenge' || tag === 'all' || !tag) {
        vars.tag = undefined
      }

      return vars
    }, [
      args,
      extraArgs,
      tag,
      tackId,
      keyword,
      authorId,
      types,
      order,
      pageSize,
      timeRangeHash,
      feed,
      userLoading,
      isPosSeq,
    ])

  const {
    data,
    loading: modelsLoading,
    fetchMore,
    loadingMore,
    refetch,
  } = useInfiniteListGenerationModels(queryArgs, {
    fetchPolicy: 'cache-first',
  })

  const hasNext = useMemo(() => {
    if (isPosSeq) return data?.pageInfo?.hasNextPage ?? false
    return data?.pageInfo?.hasPreviousPage ?? false
  }, [data?.pageInfo, isPosSeq])

  const loading = userLoading || modelsLoading

  const loadMore = useCallback(() => {
    if (!loadingMore && !modelsLoading && hasNext) {
      fetchMore({
        variables: isPosSeq
          ? ({
              after: data?.pageInfo.endCursor,
              first: pageSize,
            } as Partial<ListGenerationModelsQueryVariables>)
          : ({
              before: data?.pageInfo.startCursor,
              last: pageSize,
            } as Partial<ListGenerationModelsQueryVariables>),
      })
    }
  }, [loadingMore, modelsLoading, hasNext, fetchMore, data, isPosSeq, pageSize])

  const models = useMemo((): GenerationModel[] => {
    if (!data) return []
    const nodes = getNodesFromConnection(data)
    const positionedNodes = isPosSeq ? nodes : nodes.reverse()
    return uniqBy(positionedNodes, 'id')
  }, [data, isPosSeq])

  // Replicating previous behavior, not sure still useful or not
  useEffect(() => {
    $globalSearching.next(modelsLoading || loadingMore)
  }, [modelsLoading, loadingMore])

  /**
   * Avoid refetching when the query has already been skipped
   * Should not happen here, but keeping it to avoid BAD_USER_INPUT
   *
   * Should be revisited after updating to v4.0.11
   * @see https://github.com/apollographql/apollo-client/issues/12996
   */
  const mutateModels = useCallback(() => {
    if (queryArgs === skipToken) return
    return refetch()
  }, [queryArgs, refetch])

  return {
    models,
    loading,
    loadingMore,
    hasNext,
    loadMore,
    mutateModels,
  }
}
